import {
	ReactElement,
	Ref,
	useCallback,
	useRef,
	useState
} from "react";
import { DateString, ListItem } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { generateTrackingId, isNotNilOrWhiteSpace } from "client/Misc/utils";
import { ColoredValueModel } from "client/Api/Workspace/LiveInformation/Integration/integrationLiveInformation";
import { TimeZone } from "client/DateAndTime/timezones";
import { ColoredText } from "../ColoredText";
import { Subtitles } from "../Subtitles";
import { PlanningItemModel } from "./PlanningLiveTileContent";
import { PlanningItemDetails } from "./PlanningItemDetails";
import styles from "./PlanningListItem.module.less";

export type PlanningListItemProps = {
	item: PlanningItemModel;
	appName: string;
	timezone: TimeZone;
};

export const PlanningListItem = (props: PlanningListItemProps): ReactElement => {
	const {
		item,
		appName,
		timezone
	} = props;

	const [isViewingDetails, setIsViewingDetails] = useState<boolean>(false);

	const ref: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);

	const clickHandler = useCallback(
		(): void => {
			setIsViewingDetails(true);
		},
		[]
	);

	const closeHandler = useCallback(
		(): void => {
			setIsViewingDetails(false);
		},
		[]
	);

	const {
		title,
		startDateTime,
		endDateTime,
		subtitle1,
		subtitle2,
		subtitle3
	} = item;

	const titleValue: string | undefined = title?.value;

	const computedTitle: string = isNotNilOrWhiteSpace(titleValue)
		? titleValue
		: localize("_NoTitle");

	return (
		<>
			<ListItem
				ref={ref}
				isActive={isViewingDetails}
				trackingId={generateTrackingId("planning-live-tile-item", `|${startDateTime.value}`)}
			>
				<ListItem.Content
					onClick={clickHandler}
					tooltip={titleValue}
					thumbnail={
						<TimeSpan
							startDateTime={startDateTime}
							endDateTime={endDateTime}
							timezone={timezone}
						/>
					}
				>
					<ListItem.Title
						tooltip={computedTitle}
					>
						<ColoredText
							trackingId="title-text"
							color={title?.color}
						>
							{computedTitle}
						</ColoredText>
					</ListItem.Title>
					<Subtitles
						subTitle1={subtitle1}
						subTitle2={subtitle2}
						subTitle3={subtitle3}
					/>
				</ListItem.Content>
			</ListItem>

			{isViewingDetails && ref.current && (
				<PlanningItemDetails
					item={item}
					appName={appName}
					target={ref.current}
					onClose={closeHandler}
					timezone={timezone}
				/>
			)}
		</>
	);
};
PlanningListItem.displayName = "PlanningListItem";

type TimeSpanProps = {
	startDateTime: ColoredValueModel<string>;
	endDateTime: ColoredValueModel<string>;
	timezone: TimeZone;
};

const TimeSpan = (props: TimeSpanProps): ReactElement => {
	const {
		startDateTime,
		endDateTime,
		timezone
	} = props;

	return (
		<div className={styles.timeSpan}>
			<h4>
				<ColoredText
					trackingId="start-time"
					color={startDateTime.color}
				>
					<DateString
						value={startDateTime.value}
						format="T"
						timezone={timezone}
					/>
				</ColoredText>
			</h4>
			<div className={styles.endTime}>
				<ColoredText
					trackingId="end-time"
					color={endDateTime.color}
				>
					<DateString
						value={endDateTime.value}
						format="T"
						timezone={timezone}
					/>
				</ColoredText>
			</div>
		</div>
	);
};
TimeSpan.displayName = "TimeSpan";
